import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, propertiesAPI, autoPostAPI, socialAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import AppLayout from '../components/AppLayout';
import AccountVerificationBanner from '../components/AccountVerificationBanner';
import PendingPreviewBanner from '../components/PendingPreviewBanner';
import QuickstartChecklist from '../components/QuickstartChecklist';
import { toast } from 'sonner';
import {
  Home, Building2, Video, Instagram, Calendar, Zap, Play, Pause,
  Clock, Sparkles, Loader2, CheckCircle, AlertCircle, BarChart3,
  ArrowRight, Settings
} from 'lucide-react';

// Status badge colors
const STATUS_STYLES = {
  pending: 'bg-[#f0e2c2] text-[#9a6d1f]',
  generating_video: 'bg-[#e9efe7] text-[#1f4d3a]',
  video_ready: 'bg-[#f0e6cf] text-[#8f6f3a]',
  posting: 'bg-blue-600/20 text-blue-400',
  completed: 'bg-[#d6e8dd] text-[#1e7a4f]',
  failed: 'bg-[#a34430]/20 text-[#b3543f]',
};

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [autoPostConfig, setAutoPostConfig] = useState(null);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState({ items: [], stats: {} });
  const [socialCount, setSocialCount] = useState(0);
  const [propertyCount, setPropertyCount] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // Catch non-auth errors per call (401s are handled globally by the interceptor)
    const safeFetch = (promise, fallback) =>
      promise.catch((err) => {
        if (err.response?.status !== 401) {
          console.warn('Dashboard fetch failed:', err.message);
        }
        return { data: fallback };
      });

    try {
      const [statsRes, configRes, queueRes, historyRes, socialsRes, propsRes] = await Promise.all([
        safeFetch(dashboardAPI.getStats(), {}),
        safeFetch(autoPostAPI.getConfig(), {}),
        safeFetch(autoPostAPI.getQueue(), []),
        safeFetch(autoPostAPI.getHistory(), { items: [], stats: {} }),
        safeFetch(socialAPI.getAccounts(), { accounts: [] }),
        safeFetch(propertiesAPI.getAll(), []),
      ]);

      setStats(statsRes.data);
      setAutoPostConfig(configRes.data);
      setQueue(Array.isArray(queueRes.data) ? queueRes.data : []);
      setHistory(historyRes.data || { items: [], stats: {} });
      setSocialCount(Array.isArray(socialsRes.data?.accounts) ? socialsRes.data.accounts.length : 0);
      setPropertyCount(Array.isArray(propsRes.data) ? propsRes.data.length : 0);
    } catch (error) {
      toast.error(t('dashboard.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const isAutoPostActive = autoPostConfig?.is_enabled;
  const completedPosts = history?.stats?.total_posts || 0;
  const videoCount = stats?.total_posts || 0;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#23211a]">{t('dashboard.title')}</h1>
        </div>

        {/* Anti-abuse gating banner. Self-fetches user status and
            shows nothing if both gates pass. Sits at the top of the
            dashboard so a fresh signup sees it before anything else. */}
        <AccountVerificationBanner />

        {/* In-app counterpart to the preview email. Surfaces queue
            items in `preview_pending` waiting on the user's decision –
            so users who miss the email still find the render in-app. */}
        <PendingPreviewBanner />

        {/* Post-payment onboarding checklist. The component self-hides
            for free users + dismissed users, so it's safe to mount
            unconditionally here. Keeps the dashboard layout deterministic
            instead of branching on subscription tier. */}
        <QuickstartChecklist />

        {/* Auto Post Hero Card */}
        <Card className={`border-2 transition-all ${
          isAutoPostActive
            ? 'border-[#1e7a4f]/40 bg-gradient-to-r from-[#e4f0e8] to-[#fffdf7]/50'
            : 'border-[#d8d0bd] bg-[#fffdf7]'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  isAutoPostActive ? 'bg-[#d6e8dd]' : 'bg-[#e9e2d2]'
                }`}>
                  {isAutoPostActive ? (
                    <Play className="w-7 h-7 text-[#1e7a4f]" />
                  ) : (
                    <Pause className="w-7 h-7 text-[#8a8474]" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#23211a]">
                    {isAutoPostActive
                      ? t('dashboard.autoPostActive')
                      : t('dashboard.autoPostInactive')}
                  </h2>
                  {isAutoPostActive ? (
                    <p className="text-[#6f6a5c] text-sm mt-0.5">
                      {t('dashboard.postsPerDay', {
                        count: autoPostConfig?.posts_per_day || 1,
                        time: autoPostConfig?.preferred_time || '10:00',
                      })}
                    </p>
                  ) : (
                    <p className="text-[#8a8474] text-sm mt-0.5">
                      {t('dashboard.noPostsScheduled')}
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => navigate('/auto-post')}
                className={isAutoPostActive
                  ? 'bg-[#1e7a4f] hover:bg-[#155c3b]'
                  : 'bg-[#1f4d3a] hover:bg-[#163a2c]'
                }
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('dashboard.configure')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        {/*
          Each tappable stat card mirrors a real <button>: role+tabIndex
          for screen readers, Enter/Space activation for keyboard users,
          aria-label so the announcement reads like "Immobilien, 3" rather
          than "graphic" / nothing. Without these the cards are a totally
          mouse-only surface. Keyboard users can't reach them at all.
        */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card
            role="button"
            tabIndex={0}
            aria-label={`${t('dashboard.stats.properties')}: ${propertyCount}`}
            className="glass border-[#e3dccb] hover:border-[#1f4d3a]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f4d3a]/60 transition-all cursor-pointer"
            onClick={() => navigate('/properties')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/properties'); }
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6f6a5c] mb-1">{t('dashboard.stats.properties')}</p>
                  <p className="text-2xl font-bold text-[#23211a]">{propertyCount}</p>
                </div>
                <div className="p-2.5 bg-[#e9efe7] rounded-lg">
                  <Building2 className="w-5 h-5 text-[#1f4d3a]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            aria-label={`${t('dashboard.stats.videos')}: ${videoCount}`}
            className="glass border-[#e3dccb] hover:border-[#c7a869]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c7a869]/60 transition-all cursor-pointer"
            onClick={() => navigate('/gallery')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/gallery'); }
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6f6a5c] mb-1">{t('dashboard.stats.videos')}</p>
                  <p className="text-2xl font-bold text-[#23211a]">{videoCount}</p>
                </div>
                <div className="p-2.5 bg-[#f0e6cf] rounded-lg">
                  <Video className="w-5 h-5 text-[#8f6f3a]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            aria-label={`${t('dashboard.stats.platforms')}: ${socialCount}`}
            className="glass border-[#e3dccb] hover:border-pink-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500/60 transition-all cursor-pointer"
            onClick={() => navigate('/social')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/social'); }
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6f6a5c] mb-1">{t('dashboard.stats.platforms')}</p>
                  <p className="text-2xl font-bold text-[#23211a]">{socialCount}</p>
                </div>
                <div className="p-2.5 bg-pink-600/20 rounded-lg">
                  <Instagram className="w-5 h-5 text-pink-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-[#e3dccb]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6f6a5c] mb-1">{t('dashboard.stats.weeklyPosts')}</p>
                  <p className="text-2xl font-bold text-[#23211a]">{completedPosts}</p>
                </div>
                <div className="p-2.5 bg-[#d6e8dd] rounded-lg">
                  <BarChart3 className="w-5 h-5 text-[#1e7a4f]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Queue + Recent Posts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Queue */}
          <Card className="glass border-[#e3dccb]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#23211a] flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#1f4d3a]" />
                  {t('dashboard.queue')}
                </CardTitle>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/auto-post')}
                  className="text-[#1f4d3a] hover:text-[#1f4d3a] text-xs"
                >
                  {t('dashboard.viewAll')}
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {queue.length === 0 ? (
                <div className="text-center py-6">
                  <Calendar className="w-10 h-10 text-[#b5ad99] mx-auto mb-2" />
                  <p className="text-sm text-[#8a8474]">{t('dashboard.queueEmpty')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {queue.slice(0, 5).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#23211a] truncate">
                          {item.property_title || t('autoPost.property')}
                        </p>
                        <p className="text-xs text-[#8a8474]">
                          {item.scheduled_for
                            ? new Date(item.scheduled_for).toLocaleString()
                            : ''}
                        </p>
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ml-2 ${
                        STATUS_STYLES[item.status] || 'bg-[#c9c1ac]/20 text-[#6f6a5c]'
                      }`}>
                        {item.status === 'generating_video' && (
                          <Loader2 className="w-2.5 h-2.5 mr-0.5 animate-spin inline" />
                        )}
                        {t(`autoPost.status.${item.status}`) || item.status}
                      </span>
                    </div>
                  ))}
                  {queue.length > 5 && (
                    <p className="text-xs text-[#8a8474] text-center pt-1">
                      {t('dashboard.queueCount', { count: queue.length })}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Posts */}
          <Card className="glass border-[#e3dccb]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#23211a] flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#1e7a4f]" />
                  {t('dashboard.recentPosts')}
                </CardTitle>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/auto-post')}
                  className="text-[#1f4d3a] hover:text-[#1f4d3a] text-xs"
                >
                  {t('dashboard.viewAll')}
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(!history?.items || history.items.length === 0) ? (
                <div className="text-center py-6">
                  <Sparkles className="w-10 h-10 text-[#b5ad99] mx-auto mb-2" />
                  <p className="text-sm text-[#8a8474]">{t('dashboard.noRecentPosts')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.items.slice(0, 5).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#23211a] truncate">
                          {item.property_title || t('autoPost.property')}
                        </p>
                        <p className="text-xs text-[#8a8474]">
                          {item.completed_at
                            ? new Date(item.completed_at).toLocaleString()
                            : ''}
                        </p>
                      </div>
                      <CheckCircle className="w-4 h-4 text-[#1e7a4f] ml-2 shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
